import { SET_SCRIPTS } from '~/store/feature/Script';
import { api } from '../api';
import { SCRIPT_PATH, SCRIPT_PATH_LIST, SCRIPT_TYPES_PATH } from '../endpoints';
import buildCopyMutation from '../generics/BuildCopyMutation';
import buildDeleteMutation from '../generics/BuildDeleteMutation';
import { FormConfigFile, FormScript, FormScriptTypes, GlobalResponse } from '~/models/Commons ';
import { getHeaders } from '../generics/Header';



const extendedApiSlice = api.injectEndpoints({
  endpoints: builder => ({
    getScripts: builder.query<
      GlobalResponse<any[]>,
      {
        page: number;
        size: number;
        active: boolean;
      }
    >({
      query: ({ page, size, active}) => ({
        url: `${SCRIPT_PATH_LIST}?page=${page}&size=${size}&active=${active}`,
        method: 'GET',
        credentials: 'include',
        headers: getHeaders(),
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(SET_SCRIPTS(data.payload));
        } catch (error) {
          console.error('Error fetching scripts:', error);
        }
      },
    }),

   getscriptTypes: builder.query<
  GlobalResponse<any[]>,
  {
    page: number;
    size: number;
    active: boolean;
  }
>({
  query: ({ page, size, active }) => ({
    url: `${SCRIPT_TYPES_PATH}?page=${page}&size=${size}&active=${active}`,
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  }),
  providesTags: ["ScriptTypes"],
}),
saveScriptTypes: builder.mutation<
  GlobalResponse<FormScriptTypes>,
  {
    data: FormScriptTypes;
  }
>({
  query: ({ data }) => ({
    url: SCRIPT_TYPES_PATH,
    method: 'POST',
    body: {
      name: data.name,
      description: data.description,
    },
    credentials: 'include',
    headers: getHeaders(),
  }),
  invalidatesTags: ["ScriptTypes"],
}),
    saveScript: builder.mutation<
      GlobalResponse<FormScript>,
      {
        data: FormScript;
      }
    >({
      query: ({ data}) => {
        const formData = new FormData();

        const scriptBlob = new Blob(
          [
            JSON.stringify({
              title: data.title,
              description: data.description,
              cloudEnvironmentId: data.cloudEnvironmentId.value,
              scriptTypeId: data.scriptTypeId.value,
              outputTemplateId: data.outputTemplateId?.value,
              manual: data.manual ?? false,
            }),
          ],
          { type: 'application/json' }
        );

        formData.append('script', scriptBlob, 'script.json');

        if (data.script) {
          formData.append('file', data.script);
        }

        if (data.configFiles) {
          if (data.configFiles instanceof File) {
            formData.append('configFiles', data.configFiles);
          } else if (Array.isArray(data.configFiles)) {
            const configBlob = new Blob(
              [JSON.stringify({ configFiles: data.configFiles })],
              { type: 'application/json' }
            );
            formData.append('configFiles', configBlob, 'configFiles.json');
          }
        }

        return {
          url: SCRIPT_PATH,
          method: 'POST',
          body: formData,
          formData: true,
          credentials: 'include',
          headers: getHeaders(),
        };
      },
    }),

    addConfigFile: builder.mutation<
      GlobalResponse<FormConfigFile>,
      {
        data: FormConfigFile;
        deploymentScriptId: string;
        lang: string;
      }
    >({
      query: ({ data, deploymentScriptId }) => {
        const formData = new FormData();

        if (data.configFiles) {
          if (data.configFiles instanceof File) {
            formData.append('configFiles', data.configFiles);
          } else if (Array.isArray(data.configFiles)) {
            const configBlob = new Blob(
              [JSON.stringify({ configFiles: data.configFiles })],
              { type: 'application/json' }
            );
            formData.append('configFiles', configBlob, 'configFiles.json');
          }
        }

        formData.append('deploymentScriptId', deploymentScriptId);

        return {
          url: `${SCRIPT_PATH}/${deploymentScriptId}/configFiles`,
          method: 'POST',
          body: formData,
          formData: true,
          credentials: 'include',
          headers: getHeaders(),
        };
      },
    }),
deleteScriptType: buildDeleteMutation<FormScriptTypes, number>(builder, SCRIPT_TYPES_PATH, ["ScriptTypes"]),
  deleteScript: buildDeleteMutation<FormScript, string>(builder, SCRIPT_PATH),
    copyScript: buildCopyMutation<string>(builder, SCRIPT_PATH),
  }),
});

export const {
  useSaveScriptMutation,
  useSaveScriptTypesMutation,
  useAddConfigFileMutation,
  useGetScriptsQuery,
  useGetscriptTypesQuery,
  useCopyScriptMutation,
  useLazyGetScriptsQuery,
  useDeleteScriptMutation,
  useDeleteScriptTypeMutation,
} = extendedApiSlice;